import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {cloneDeep} from 'lodash';
import {injectIntl} from 'react-intl';
import Moment from 'moment';
import constants, {Colors} from '../../constants';
import SettingsLabelsLineItem from '../../components/settings-labels/settings_labels_line_item';
import CreateLabelMutation from '../../mutations/create_label_mutation';
import ColorsPicker from '../../components/colors_picker';
import * as tracking from '../../tracking';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import Util from '../../forecast-app/shared/util/util';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';

class settingsLabels extends Component {
	constructor(props) {
		super(props);
		let colors = [];
		for (const key in Colors) {
			if (Object.prototype.hasOwnProperty.call(Colors, key)) {
				const label = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
				colors.push({value: key, label: label, hex: Colors[key]});
			}
		}

		// ascending value as a string
		const ascending = localStorage.getItem('overview-projects-sort-ascending') || 'true';
		// Set initial sort
		const sortBy = {
			column: localStorage.getItem('overview-projects-sort-column') || null,
			ascending: ascending === 'true',
		};

		const labels = cloneDeep(props.viewer.company.labels.edges);
		this.sortLabels(labels, sortBy);
		const labelsOrder = labels.map(label => label.node.id);

		this.state = {
			sortBy: sortBy,
			newLabelName: '',
			newLabelColor: constants.colors[0],
			labelsOrder,
		};
	}

	componentDidMount() {
		document.title = 'Labels - Account - Forecast';
		// Segment
		tracking.trackPage('settings-labels');
	}

	UNSAFE_componentWillUpdate(nextProps, nextState) {
		if (nextState.sortBy.column !== this.state.sortBy.column || nextState.sortBy.ascending !== this.state.sortBy.ascending) {
			const labels = cloneDeep(nextProps.viewer.company.labels.edges);
			this.sortLabels(labels, nextState.sortBy);
			const labelsOrder = labels.map(label => label.node.id);
			this.setState({labelsOrder});
		}
	}

	handleNewLabelNameChange(e) {
		if (!e.target.value.includes('#') && !e.target.value.includes(' ')) this.setState({newLabelName: e.target.value});
	}

	handleAddLabel() {
		if (this.state.newLabelName !== '') {
			const onSuccess = result => {
				this.setState({
					newLabelName: '',
					newLabelColor: constants.colors[0],
				});
			};

			Util.CommitMutation(
				CreateLabelMutation,
				{
					companyId: this.props.viewer.company.id,
					name: this.state.newLabelName,
					color: this.state.newLabelColor,
				},
				onSuccess,
				true,
				false
			);
		}
	}

	handleSelectColorChange(value) {
		this.setState({newLabelColor: value});
	}

	setSortBy(column) {
		let ascending = true;
		if (column === this.state.sortBy.column) {
			ascending = !this.state.sortBy.ascending;
		}
		Util.localStorageSetItem('overview-projects-sort-column', column);
		Util.localStorageSetItem('overview-projects-sort-ascending', ascending);
		this.setState({sortBy: {column: column, ascending: ascending}});
	}

	sortLabels(labels, sortBy) {
		const sortByName = (a, b) => {
			a = a.node;
			b = b.node;
			if (a.name.toLowerCase() < b.name.toLowerCase()) return sortBy.ascending ? -1 : 1;
			if (a.name.toLowerCase() > b.name.toLowerCase()) return sortBy.ascending ? 1 : -1;
			else return 0;
		};

		const sortByColor = (a, b) => {
			a = a.node;
			b = b.node;
			if (a.color < b.color) return sortBy.ascending ? -1 : 1;
			if (a.color > b.color) return sortBy.ascending ? 1 : -1;
			else return 0;
		};

		const sortByUsages = (a, b) => {
			a = a.node;
			b = b.node;
			if (a.occurrenceCount < b.occurrenceCount) return sortBy.ascending ? -1 : 1;
			if (a.occurrenceCount > b.occurrenceCount) return sortBy.ascending ? 1 : -1;
			else return 0;
		};

		const sortMapping = new Map([
			['name', sortByName],
			['color', sortByColor],
			['usages', sortByUsages],
		]);
		labels.sort(sortMapping.get(sortBy.column));
	}

	getHeader() {
		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={this.props.intl.formatMessage({id: 'common.labels'})} />
			</TopHeaderBarWrapper>
		);
	}

	render() {
		const {formatMessage} = this.props.intl;
		const labels = [];
		this.state.labelsOrder.forEach(labelId => {
			const label = this.props.viewer.company.labels.edges.find(labelNode => labelNode.node.id === labelId);
			if (label) {
				labels.push(label);
			}
		});
		this.props.viewer.company.labels.edges.filter(label => !this.state.labelsOrder.includes(label.node.id)).forEach(label => labels.push(label));
		const createdAt = Moment(this.props.viewer.company.createdAt, 'x');
		const transitionDay = Moment('2021-03-24'); //from this day people can choose a custom color in the project settings
		const isOldCompany = createdAt.isBefore(transitionDay);
		return (
			<div className="section-content settings-app settings-labels" data-cy={'settings-labels-page'}>
				{this.props.children}
				{this.getHeader()}
				<CustomScrollDiv>
					<div className={'section-body'}>
						<div className="inner">
							<fieldset className="general">
								<p className="description">{formatMessage({id: 'settings_labels.label-description'})}</p>
								<table>
									<thead>
										<tr>
											{['name', 'color', 'usages'].map((colType, index) => (
												<th className={`sortable ${this.state.sortBy.column === colType ? (this.state.sortBy.ascending ? 'ascending' : 'descending') : ''}`} onClick={this.setSortBy.bind(this, colType)} key={index}>
													{formatMessage({id: `settings_labels.${colType}`})}
													<span>&nbsp;&nbsp;</span>
												</th>
											))}
											<th>&nbsp;</th>
										</tr>
									</thead>
									<tbody>
										{labels.map(label => (
											<SettingsLabelsLineItem
												cy={'label-name'}
												key={label.node.id}
												label={label.node}
												colors={constants.colors}
												viewerId={this.props.viewer.id}
												companyId={this.props.viewer.company.id}
												isOldCompany={isOldCompany}
											/>
										))}
										<tr>
											<td>
												<input
													data-cy="new-label-name-input"
													type="text"
													value={this.state.newLabelName}
													onChange={this.handleNewLabelNameChange.bind(this)}
													placeholder={formatMessage({
														id: 'settings_labels.label-name',
													})}
												/>
											</td>
											<td>
												<ColorsPicker value={this.state.newLabelColor} colors={constants.colors} onColorSelect={this.handleSelectColorChange.bind(this)} isOldCompany={isOldCompany} />
											</td>
											<td>&nbsp;</td>
											<td>
												<button data-cy="add-label-button" className="submit-button" onClick={this.handleAddLabel.bind(this)}>
													{formatMessage({id: 'settings_labels.add-label'})}
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</fieldset>
						</div>
					</div>
				</CustomScrollDiv>
			</div>
		);
	}
}

settingsLabels.propTypes = {};

export const settingsLabelsQuery = graphql`
	query settingsLabels_Query {
		viewer {
			actualPersonId
			component(name: "settings_labels")
			...settingsLabels_viewer
		}
	}
`;

graphql`
	fragment settingsLabels_label on Label {
		id
		name
		color
		occurrenceCount
	}
`;

export default injectIntl(
	createFragmentContainer(settingsLabels, {
		viewer: graphql`
			fragment settingsLabels_viewer on Viewer {
				actualPersonId
				id
				availableFeatureFlags {
					key
				}
				company {
					id
					createdAt
					labels(first: 1000) @connection(key: "Company_labels") {
						edges {
							node {
								id
								...settingsLabels_label @relay(mask: false)
							}
						}
					}
				}
			}
		`,
	})
);
